import {
  Button,
  Flex,
  Modal,
  type ModalProps,
  Space,
  Table,
  Text,
} from '@/components';
import { MAXIMUM_SEARCH_HISTORY_LIMIT } from '@/features/project/_utils/constants';
import { SEARCH_DEBOUNCE_MS_TIME } from '@/features/search/_utils/constants';
import { TABLE_BUTTON_WIDTH } from '@/utils';
import dayjs from 'dayjs';
import { type FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchHistory } from '../../_hooks/search-projects/local-storage';
import { useProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';

type Props = ModalProps & {
  onClose: () => void;
  open: boolean;
  onSearch: () => void;
};

export const SearchHistoryModal: FC<Props> = ({ onClose, open, onSearch }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { setSearchQueryStates } = useProjectsPageSearchQueryStates();
  const { searchHistoryData, setSearchHistoryStorage } = useSearchHistory();

  return (
    <Modal
      open={open}
      width={1200}
      onCancel={onClose}
      title={t('search-history')}
      footer={<Button onClick={onClose}>{t('close')}</Button>}
      loading={isLoading}
    >
      <Flex vertical gap={16}>
        <Text
          text={t('search-history-limit-information', {
            limit: MAXIMUM_SEARCH_HISTORY_LIMIT,
          })}
        />
        <Table
          enableSticky={false}
          dataSource={searchHistoryData?.sort((a, b) =>
            dayjs(b.datetime).isAfter(a.datetime) ? 1 : -1,
          )}
          scroll={{ y: 400 }}
          columns={[
            {
              title: t('search-history-datetime'),
              dataIndex: 'datetime',
              width: 100,
              render: (value) => dayjs(value).format('YYYY/MM/DD HH:mm'),
            },
            {
              title: t('search-condition'),
              dataIndex: 'searchHistoryLabelsMap',
              width: 400,
              render: (_, record) => (
                <SearchHistoryLabels
                  searchHistoryLabelsMap={record.searchHistoryLabelsMap}
                />
              ),
            },
            {
              title: '',
              key: 'actions',
              width: TABLE_BUTTON_WIDTH,
              render: (_, record) => (
                <Button
                  type="link"
                  onClick={async () => {
                    setIsLoading(true);
                    onSearch();
                    setSearchQueryStates((prev) => {
                      // 既存のパラメータをすべてnullに設定
                      const clearedState = Object.fromEntries(
                        Object.keys(prev).map((key) => [key, null]),
                      );
                      // 新しい検索条件を適用
                      return {
                        ...clearedState,
                        ...record.filterConditions,
                      };
                    });
                    setSearchHistoryStorage(
                      record.filterConditions,
                      record.searchHistoryLabelsMap,
                    );
                    // nuqsの反映遅延を考慮してsleep処理を入れた。
                    setTimeout(() => {
                      setIsLoading(false);
                      onClose();
                    }, SEARCH_DEBOUNCE_MS_TIME);
                  }}
                >
                  {t('search')}
                </Button>
              ),
            },
          ]}
        />
      </Flex>
    </Modal>
  );
};

const SearchHistoryLabels = ({
  searchHistoryLabelsMap,
}: {
  searchHistoryLabelsMap:
    | {
        label: string;
        value: string | number;
      }[]
    | undefined;
}) => {
  return searchHistoryLabelsMap?.flatMap(({ label, value }, index, array) => {
    if (!value) {
      return [];
    }
    return (
      <Fragment key={`${label}_${value}`}>
        <Space
          size={4}
          wrap
          split=":"
          style={{
            marginRight: index < array.length - 1 ? 16 : undefined,
          }}
        >
          <Text text={label} color="tertiary" />
          <Flex align="center" gap={4}>
            <Text text={value.toString()} />
          </Flex>
        </Space>
      </Fragment>
    );
  });
};
